import {
  minifyGraphQL,
  parseGraphQlResponse,
  validateResponse,
  type GraphQlResult,
} from '../utils';

interface ExecuteGraphQlRequestParams {
  cmsUrl: string;
  query: string;
  variables?: Record<string, unknown>;
  headers?: Record<string, string>;
}

const isTimeout = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === 'AbortError' || error.name === 'TimeoutError');

const requestGraphQl = async ({
  cmsUrl,
  query,
  headers,
  variables,
}: ExecuteGraphQlRequestParams): Promise<Response> => {
  const endpoint = `${cmsUrl}/graphql`;
  const minifiedQuery = minifyGraphQL(query);
  let url = `${endpoint}?query=${encodeURIComponent(minifiedQuery)}`;

  if (variables && Object.keys(variables).length > 0) {
    const encodedVariables = encodeURIComponent(JSON.stringify(variables));
    url += `&variables=${encodedVariables}`;
  }

  try {
    return await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...headers },
      signal: AbortSignal.timeout(10000),
    });
  } catch (error) {
    if (isTimeout(error)) {
      throw new Error('The request timed out.');
    }
    throw error;
  }
};

const executeGraphQlRequest = async <T = unknown>(
  params: ExecuteGraphQlRequestParams,
): Promise<T> => validateResponse<T>(await requestGraphQl(params));

/**
 * Like `executeGraphQlRequest`, but also returns field-level errors that came
 * back alongside partial data, so callers can tell a null field that
 * WordPress failed to resolve from one that does not exist.
 */
export const executeGraphQlRequestWithErrors = async <T = unknown>(
  params: ExecuteGraphQlRequestParams,
): Promise<GraphQlResult<T>> =>
  parseGraphQlResponse<T>(await requestGraphQl(params));

export default executeGraphQlRequest;
